import { useMemo, useState } from 'react';
import Page from '../../../components/PageWrapper';
import SelectMenu from '../../../components/form-control/SelectMenu';
import { GetDashboardGenericsData, getDashboardGenericsDataAgent, getPendingPayments, getTeamDashboardMetrics } from '../../../api/requests';
import { SelectedDateRange } from '../../../components/dashboard/date-range-selector/datePicker';
import { IGeneralMetrics, ITeamAdminMetrics } from '../../../models/generalMetrics/IGeneralMetrics';
import GTable from '../../../components/elements/GTable';
import DashboardTabs from '../../../components/dashboard/tabs/DashboardTabs';
import DoughnutChart from '../../../components/dashboard/charts/DonutChart';
import moment from 'moment';
import Card from '../../../components/dashboard/card/card';
import { Link, useNavigate } from 'react-router-dom';
import { putDot } from '../../../utils/miscFunctions';
import useRoute from '../../../hooks/routesHook';
import { User } from '../../../models/User';
import useUserAccount from '../../../hooks/userAccountHook';
import useModal from '../../../hooks/modalHook';
import QuoteIframe from '../../../components/dashboard/quoteWidget/QuoteWidget';
import PayoutsView from '../../partners/shared/payouts/index';
import { Payout } from '../../../models/payout/Payout';
import SalesSummaryOld from '../../../components/dashboard/sales-summary/SalesSummaryOld';
import TeamsSalesBarChart from '../../../components/dashboard/charts/TeamSalesBarChart';
import { ITeamMetricsDTO } from '../../../resources/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import usePolicy from '../../../hooks/policyHook';
import { MdPolicy } from 'react-icons/md';
import Button from '../../../components/form-control/Button';

function AgentsDashboard() {
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const { getProfile } = useUserAccount();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { addQuote, PolicyModalComponent } = usePolicy();
  const user = new User(getProfile());
  const [loading, setLoading] = useState<boolean>(false);
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>({
    from: '',
    to: ''
  });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);
  const [bonus, setBonus] = useState({
    quarterly: 150,
    annual: 500
  });
  const [generalMetrics, setGeneralMetrics] = useState<IGeneralMetrics>({
    arpc: 0,
    cac: 0,
    churn: 0,
    csatScore: 0,
    averageRevenue: 0,
    referralRate: 0,
    nsm: 0,
    conversionRate: 0,
    grossMargin: 0,
    mmr: 0,
    pcr: 0,
    totalRevenue: 0,
    leads: 0,
    payments: 0,
    policy: 0,
    sales: 0,
    customers: 0,
    customerCount: 0,
    ltv: 0,
    rgr: 0,
    gmv: 0,
    arr: 0,
    roms: 0,
    cpl: 0,
    leadCount: 0,
    policyCount: 0,
    leads_channel: {
      bolt: 0,
      yango: 0,
      whatsapp: 0,
      referral: 0,
      facebook: 0,
      direct: 0,
      telegram: 0,
      google: 0,
      instagram: 0,
      radio: 0
    },
    customer_channel: {
      whatsapp: 20,
      web: 10,
      facebook: 50,
      telegram: 50,
      google: 40,
      instagram: 40
    },
    sales_channel: {
      whatsapp: 0,
      web: 0,
      facebook: 0,
      telegram: 0,
      google: 0,
      instagram: 0
    },
    leads_status: {
      warm: 0,
      paid: 0,
      closed: 0,
      cold: 0
    },
    payment_amount_upcoming_this_week: 0,
    total_outstanding_payments: 0,
    all_overdue_payment_amount: 0,
    payment_amount_due_this_month: 0,
    payment_amount_due_this_week: 0,
    payment_amount_due_today: 0,
    sales_mix: {
      amount: {
        comprehensive_90: 30,
        comprehensive_85: 10,
        comprehensive_100: 20,
        'third-party-ft': 50,
        'third-party': 20
      },
      count: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        'third-party-ft': 0,
        'third-party': 0
      }
    },
    leads_tags: {
      CONTACTED: 0,
      CONVERTED: 0,
      DISQUALIFIED: 0,
      IN_NEGOTIATION: 0,
      NEW: 0,
      NURTURING: 0,
      QUALIFIED: 0,
      UNRESPONSIVE: 0
    }
  });
  const [teamMetrics, setTeamMetrics] = useState<ITeamAdminMetrics>({
    mom: {
      count: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      },
      sales: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      }
    },
    sales: {
      this_month: 0,
      today: 0,
      this_week: 0,
      this_year: 0
    },
    products: {
      'Auto Insurance Sales': 0,
      'Home Insurance Sales': 0
    },
    salesMix: {
      amount: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      },
      count: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      }
    }
  });
  const [selectedYear, setSelectedYear] = useState<string>(String(new Date()?.getFullYear()));
  const [salesMix, setSalesMix] = useState({
    amount: {
      Comprehensive: 20,
      'Third Party': 40
    },
    count: {
      Comprehensive: 0,
      'Third Party': 0
    }
  });
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const generalMetricsSalesMix = () => {
    if (generalMetrics.sales_mix) {
      setSalesMix({
        ...salesMix,
        count: {
          Comprehensive:
            generalMetrics.sales_mix.count.comprehensive_100 +
            generalMetrics.sales_mix.count.comprehensive_90 +
            generalMetrics.sales_mix.count.comprehensive_85,
          'Third Party':
            generalMetrics.sales_mix.count['third-party'] +
            generalMetrics.sales_mix.count['third-party-ft']
        }
      });
    }
  }
  const onChange = (dateRange: { from: string; to: string }) => {
    setSelectedDateRange(dateRange);
  };

  const getTeamMetricsData = () => {
    setLoading(true);

    const params: ITeamMetricsDTO = { ...selectedDateRange, year: selectedYear };
    getTeamDashboardMetrics(countryId, params)
      .then((res) => setGeneralMetrics(res))
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const getData = () => {
    setLoading(true);

    const getMetrics = user.isAgent()
      ? getDashboardGenericsDataAgent(countryId, selectedDateRange)
      : GetDashboardGenericsData(countryId, selectedDateRange);

    Promise.allSettled([getMetrics, getPendingPayments(1, 10)])
      .then((res) => {
        const [genericsData, paymentsData] = res;

        if (genericsData.status == 'fulfilled') {
          setGeneralMetrics(genericsData.value.data?.data);
        }

        if (paymentsData.status == 'fulfilled') {
          const payments = paymentsData.value.data?.data || [];
          const slice = payments.slice(0, 20);
          setPendingPayments(slice);
          setPendingPaymentsCount(payments.length);
        }
      })
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
  };



  useQuery({ queryKey: ['getData', selectedDateRange, countryId], queryFn: getData });
  useQuery({ queryKey: ['getTeamMetricsData', selectedDateRange, countryId, selectedYear], queryFn: getTeamMetricsData });
  useQuery({ queryKey: ['generalMetricsSalesMix', generalMetrics], queryFn: generalMetricsSalesMix });

  const pendingPaymentColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Due Date',
            accessor: 'dueDate',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Policy ID',
            accessor: 'policyNumber',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Amount',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ? putDot(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Reference',
            accessor: 'reference',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Channel',
            accessor: 'channel',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}</span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  return (
    <Page title="Dashboard">
      <div className="mx-0 md:mx-8 bg-white min-h-full p-5">
        <div className="flex justify-between max-[320px]:flex-col max-[320px]:items-center mb-4">
          <h1 className="font-semibold text-3xl">Dashboard</h1>

          <div className="flex items-center gap-3">
            <div className="w-40">
              <SelectMenu
                label=""
                name="years"
                listSelectedValue={selectedYear}
                onChange={(val: string) => setSelectedYear(val)}
                list={['2024', '2025', '2026']}
              />
            </div>
            <Button
              icon={<MdPolicy className='h-4 w-4' />}
              onClick={() => addQuote()}
              className="rounded-md border bg-[#FCF6E9] border-[#FBC02D] px-3 py-2 flex items-center gap-1"
              label='New Policy'
            />
          </div>
        </div>

        <SalesSummaryOld generalMetrics={generalMetrics} onDateRangeChange={onChange} />

        <TeamsSalesBarChart teamMetrics={teamMetrics} />

        {!user.isAgent() && (
          <div className="rounded-md h-full mt-10 mb-1 justify-between px-2 pb-10 pt-7 align-items-center">
            <div className="flex justify-between mb-2">
              <h2 className="font-bold text-lg pt-1">Commission Payouts</h2>

              {payouts.length > 0 && (
                <Link to={`${getDashboardBase()}/financials`}>
                  <button className="bg-[#FFEBB8] px-4 py-2 rounded-md border border-[#FBC02D] text-sm">
                    View All
                  </button>
                </Link>
              )}
            </div>
            <div className="overflow-x-auto shadow-md mt-1 ">
              <PayoutsView onPayoutsUpdated={(payouts: Payout[]) => setPayouts(payouts)} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2  gap-6 xl:gap-16 mt-8 mb-8">
          <div className="bg-white rounded-md overflow-hidden shadow-full p-6 sm:p-12 lg:p-4 xl:p-12 min-h-40 h-fit">
            <DashboardTabs
              tabGroupTitle="Sales Mix"
              tab1={{
                name: 'Amount',
                children: (
                  <DoughnutChart chartKey={'leads_status'} metricsData={salesMix.amount} />
                )
              }}
              tab2={{
                name: 'Count',
                children: (
                  <DoughnutChart chartKey={'leads_channel'} metricsData={salesMix.count} />
                )
              }}
              activeTab={'Count'}
            />
          </div>
          <div className="bg-white rounded-md overflow-hidden shadow-full p-6 sm:p-12 lg:p-4 xl:p-12 min-h-40 h-fit">
            <div className="title pb-4 text-3xl lg:text-lg font-bold">Signups</div>
            <DoughnutChart
              chartKey={'leads_status'}
              metricsData={generalMetrics['leads_status']}
            />
          </div>
        </div>

        {!user.isAgent() && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-16 my-5 mb-1">
            <Card className="row-span-2">
              <div className="flex justify-between">
                <div className="flex-auto w-64">
                  <h2 className="text-base ">Quarterly Bonus </h2>
                </div>
                <h2 className="text-base font-bold ">¢{bonus.quarterly}</h2>
              </div>
            </Card>
            <Card className="row-span-2">
              <div className="flex justify-between">
                <div className="flex-auto w-64">
                  <h2 className="text-base">Annual Bonus</h2>
                </div>
                <h2 className="text-base font-bold">¢{bonus.annual}</h2>
              </div>
            </Card>
          </div>
        )}

        {!user.isAgent() && (
          <div className="rounded-md h-full mt-10 mb-1 justify-between px-2 pb-3 pt-7 align-items-center">
            <div className="flex justify-between mb-3">
              <h2 className="font-bold text-lg pt-1">Pending Payments</h2>
              <div className="flex flex-col md:flex-row justify-between items-center gap-x-2">
                {pendingPayments.length > 0 && (
                  <span className="whitespace-nowrap">
                    Showing {pendingPayments.length} of {pendingPaymentsCount}
                  </span>
                )}
                {pendingPayments.length > 0 && (
                  <Link to={`${getDashboardBase()}/payments`}>
                    <button
                      onClick={() => navigate(`${getDashboardBase()}`, { replace: true })}
                      className="bg-[#FFEBB8] px-4 py-2 rounded-md text-sm whitespace-nowrap">
                      View All
                    </button>
                  </Link>
                )}
              </div>
            </div>
            <div className="overflow-x-auto mt-4">
              <GTable
                classes="h-full py-6"
                columns={pendingPaymentColumns}
                data={pendingPayments}
                tableType={'payment'}
                showSearch={false}
                onRowClick={(payment: any) => {
                  navigate(`${getDashboardBase()}/policies/${payment.policyNumber}?type=motor`, {
                    replace: true
                  });
                }}
                loading={loading}
              />
            </div>
          </div>
        )}

      </div>
      <PolicyModalComponent />
      <ModalComponent />
    </Page>
  );
}

export default AgentsDashboard;
